import jsPDF from "jspdf";
import "jspdf-autotable";
import type { RowInput } from "jspdf-autotable";
import type { ExcursionStatus } from "api/excursions/Excursion.typed";
import type { MultiComment } from "api/zekay/comments";

import { isPersonPicklistParamType } from "../../template/display/paramsTemplateUtils";

type SearchParamItem = { id: number; label: string };

/** Second column cell: person name + optional PDF comment (drawn in italics in didDrawCell). */
export type PdfPersonCommentCell = {
  content: string;
  pdfPersonComment: string;
};

export type PdfGroupData = {
  index: number;
  color?: string | null;
  users: { label: string; color?: string | null; pdfComment?: string }[];
  clients: { label: string; color?: string | null; pdfComment?: string }[];
  params: Record<string, unknown>;
  paramsTemplate?: Record<string, string>;
  comments: MultiComment[];
};

export type PdfExcursionData = {
  name: string;
  date: string;
  startTime?: string;
  endTime?: string;
  status?: ExcursionStatus;
  color?: string;
  params: Record<string, unknown>;
  paramsTemplate: Record<string, string>;
  groups: PdfGroupData[];
  companyName?: string;
};

const STATUS_LABELS: Record<string, string> = {
  scheduled: "Planifiée",
  validated: "Validée",
  cancelled: "Annulée",
};

function hexToRgb(hex: string): [number, number, number] {
  const h = hex.replace("#", "");
  const n = parseInt(
    h.length === 3
      ? h
          .split("")
          .map((c) => c + c)
          .join("")
      : h,
    16,
  );
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
}

function safeColor(
  color?: string | null,
  fallback: [number, number, number] = [100, 100, 100],
): [number, number, number] {
  return color && color.trim().startsWith("#") ? hexToRgb(color) : fallback;
}

function isColorDark(rgb: [number, number, number]): boolean {
  return (rgb[0] * 299 + rgb[1] * 587 + rgb[2] * 114) / 1000 < 140;
}

function formatParamValue(value: unknown, type?: string): string {
  if (value == null || value === "") return "";
  if (typeof value === "boolean") return value ? "Oui" : "Non";
  if (isPersonPicklistParamType(type ?? "")) {
    const arr = Array.isArray(value) ? value : [value];
    return (
      arr
        .filter((v): v is SearchParamItem => v != null && typeof v === "object" && "label" in v)
        .map((v) => v.label)
        .join(", ") || "—"
    );
  }
  return String(value);
}

function formatDate(iso: string): string {
  try {
    const d = new Date(iso);
    return d.toLocaleDateString("fr-FR", {
      weekday: "long",
      day: "numeric",
      month: "long",
      year: "numeric",
    });
  } catch {
    return iso;
  }
}

function isPdfPersonCommentCell(raw: unknown): raw is PdfPersonCommentCell {
  if (raw == null || typeof raw !== "object") return false;
  const o = raw as Record<string, unknown>;
  return (
    typeof o.content === "string" &&
    typeof o.pdfPersonComment === "string" &&
    o.pdfPersonComment.trim().length > 0
  );
}

function pdfCommentTextForDisplay(rawComment: string): string {
  return `(${rawComment.trim()})`;
}

/** Matches jspdf-autotable `autoTableText` vertical start so custom text stacks with other cells. */
function autoTableFirstLineY(
  doc: jsPDF,
  textPosY: number,
  valign: string | undefined,
  lineCount: number,
): number {
  const PHYSICAL_LINE_HEIGHT = 1.15;
  const k = doc.internal.scaleFactor;
  const fontSizeMm = doc.getFontSize() / k;
  const lineHeightFactor =
    typeof doc.getLineHeightFactor === "function"
      ? doc.getLineHeightFactor()
      : PHYSICAL_LINE_HEIGHT;
  const lineHeight = fontSizeMm * lineHeightFactor;
  let y = textPosY + fontSizeMm * (2 - PHYSICAL_LINE_HEIGHT);
  const v = valign ?? "top";
  if (v === "middle") {
    y -= (lineCount / 2) * lineHeight;
  } else if (v === "bottom") {
    y -= lineCount * lineHeight;
  }

  return y;
}

type PdfDrawCell = {
  getTextPos: () => { x: number; y: number };
  width: number;
  padding: (name: string) => number;
  styles: { valign?: string };
  text: string[];
};

/** Name (normal) + comment (italic); uses cell inner width and same baseline rules as autoTable. */
function drawPersonNameWithItalicComment(
  doc: jsPDF,
  cell: PdfDrawCell,
  name: string,
  comment: string,
  textRgb: [number, number, number],
): void {
  const trimmed = comment.trim();
  if (!trimmed) return;
  const displayComment = pdfCommentTextForDisplay(trimmed);

  const innerW = Math.ceil(cell.width - cell.padding("left") - cell.padding("right"));
  const textPos = cell.getTextPos();
  const lineCount = Math.max(1, cell.text.length);
  const k = doc.internal.scaleFactor;
  const fontSizeMm = doc.getFontSize() / k;
  const lineHeightFactor =
    typeof doc.getLineHeightFactor === "function" ? doc.getLineHeightFactor() : 1.15;
  const lh = fontSizeMm * lineHeightFactor;

  let y = autoTableFirstLineY(doc, textPos.y, cell.styles.valign, lineCount);
  doc.setTextColor(textRgb[0], textRgb[1], textRgb[2]);

  doc.setFont("helvetica", "normal");
  const nameLines = doc.splitTextToSize(name, innerW);
  const x0 = textPos.x;

  if (nameLines.length === 1) {
    doc.text(nameLines[0], x0, y);
    const gap = "  ";
    const afterNameX = x0 + doc.getTextWidth(nameLines[0] + gap);
    const widthAfterName = x0 + innerW - afterNameX;
    doc.setFont("helvetica", "italic");
    if (widthAfterName < 12) {
      const cLines = doc.splitTextToSize(displayComment, innerW);
      for (let i = 0; i < cLines.length; i++) {
        doc.text(cLines[i], x0, y + lh * (i + 1));
      }

      return;
    }
    const firstPart = doc.splitTextToSize(displayComment, widthAfterName)[0] ?? "";
    doc.text(firstPart, afterNameX, y);
    const rest = displayComment.slice(firstPart.length).trimStart();
    if (!rest) return;
    const more = doc.splitTextToSize(rest, innerW);
    for (let i = 0; i < more.length; i++) {
      doc.text(more[i], x0, y + lh * (i + 1));
    }

    return;
  }

  for (const ln of nameLines) {
    doc.setFont("helvetica", "normal");
    doc.text(ln, x0, y);
    y += lh;
  }
  doc.setFont("helvetica", "italic");
  const cLines = doc.splitTextToSize(displayComment, innerW);
  for (const ln of cLines) {
    doc.text(ln, x0, y);
    y += lh;
  }
}

export function generateExcursionPdf(data: PdfExcursionData): void {
  const doc = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4" });
  const pageW = doc.internal.pageSize.getWidth();
  const margin = 14;
  const contentW = pageW - margin * 2;
  const lw = 0.15;
  let y = margin;

  const addPage = () => {
    doc.addPage();
    y = margin;
  };
  const checkPage = (needed: number) => {
    if (y + needed > doc.internal.pageSize.getHeight() - margin) addPage();
  };

  const excursionColor = safeColor(data.color, [252, 203, 6]);
  const excursionHeaderText: [number, number, number] = isColorDark(excursionColor)
    ? [255, 255, 255]
    : [30, 30, 30];
  const textColor: [number, number, number] = [40, 40, 40];
  const mutedColor: [number, number, number] = [120, 120, 120];

  // ── Header: colored band (title / date / status / time); excursion-level params as table below ──
  const excParamKeys = Object.entries(data.paramsTemplate);
  const headerBaseH = 28;

  doc.setFillColor(...excursionColor);
  doc.rect(0, 0, pageW, headerBaseH, "F");

  doc.setFontSize(18);
  doc.setTextColor(...excursionHeaderText);
  doc.setFont("helvetica", "bold");
  doc.text(data.name, margin, 12);
  doc.setFontSize(10);
  doc.setFont("helvetica", "normal");
  const statusText = STATUS_LABELS[data.status ?? ""] ?? data.status ?? "";
  doc.text(`${formatDate(data.date)}   ${statusText}`, margin, 19);
  const timeStr = [data.startTime, data.endTime].filter(Boolean).join(" – ");
  if (timeStr) doc.text(timeStr, margin, 24);

  if (excParamKeys.length > 0) {
    const excParamLabelColW = 52;
    const paramTableGapBelowHeaderMm = 5;
    const paramValueRowMinHeightMm = 12;
    (doc as any).autoTable({
      startY: headerBaseH + paramTableGapBelowHeaderMm,
      body: excParamKeys.map(([key, type]) => {
        const display = formatParamValue(data.params[key], type).trim();
        return [key, display];
      }),
      margin: { left: margin, right: margin },
      tableLineColor: [210, 210, 210],
      tableLineWidth: lw,
      columnStyles: {
        0: {
          cellWidth: excParamLabelColW,
          fontStyle: "bold",
          textColor: [80, 80, 80],
          valign: "middle",
        },
        1: { cellWidth: contentW - excParamLabelColW, minCellHeight: paramValueRowMinHeightMm },
      },
      styles: {
        fontSize: 8.5,
        cellPadding: { top: 2.5, bottom: 2.5, left: 7, right: 2.5 },
        textColor: textColor,
        lineColor: [210, 210, 210],
        lineWidth: lw,
        overflow: "linebreak",
        fillColor: [255, 255, 255],
      },
      theme: "grid",
    });
    y = (doc as any).lastAutoTable.finalY + 10;
  } else {
    y = headerBaseH + 10;
  }

  const dotR = 2;

  function drawDot(x: number, cy: number, rgb: [number, number, number]) {
    doc.setFillColor(...rgb);
    doc.circle(x + dotR, cy, dotR, "F");
  }

  // ── Groups ──
  for (const group of data.groups) {
    const groupColor = safeColor(group.color);

    checkPage(20);

    // Group title: dot + "Groupe N"
    drawDot(margin, y, groupColor);
    doc.setFontSize(11);
    doc.setFont("helvetica", "bold");
    doc.setTextColor(...textColor);
    doc.text(`Groupe ${group.index + 1}`, margin + dotR * 2 + 3, y + 1);
    y += 7;

    const body: RowInput[] = [];
    /** Per body row: dot color on column 1 (guides / customers), or null. */
    const rowDotRgb: Array<[number, number, number] | null> = [];

    const labelCellStyles = {
      valign: "middle" as const,
      textColor: [80, 80, 80] as [number, number, number],
      fontStyle: "normal" as const,
    };

    const appendPeopleRows = (
      people: { label: string; color?: string | null; pdfComment?: string }[],
      sectionLabel: string,
      fallbackRgb: [number, number, number],
    ) => {
      if (people.length === 0) return;
      const n = people.length;
      people.forEach((p, i) => {
        const dotRgb = safeColor(p.color ?? group.color, fallbackRgb);
        const trimmedComment = p.pdfComment?.trim();
        const rightCell =
          trimmedComment != null && trimmedComment !== ""
            ? { content: p.label, pdfPersonComment: trimmedComment }
            : p.label;
        if (i === 0) {
          body.push([{ content: sectionLabel, rowSpan: n, styles: labelCellStyles }, rightCell]);
        } else {
          body.push([rightCell]);
        }
        rowDotRgb.push(dotRgb);
      });
    };

    appendPeopleRows(
      group.users,
      group.users.length === 1 ? "Encadrant" : "Encadrants",
      groupColor,
    );
    appendPeopleRows(group.clients, group.clients.length === 1 ? "Client" : "Clients", groupColor);

    const templateKeys = Object.entries(group.paramsTemplate ?? data.paramsTemplate);
    for (const [key, type] of templateKeys) {
      const value = group.params[key];
      body.push([key, formatParamValue(value, type)]);
      rowDotRgb.push(null);
    }

    if (group.comments.length > 0) {
      const commentText = group.comments
        .map((c) => {
          const dateStr = c.Date ? new Date(c.Date).toLocaleDateString("fr-FR") : "";
          return `${c.User ?? "Inconnu"} (${dateStr}) : ${c.Comment ?? ""}`;
        })
        .join("\n");
      body.push(["Commentaires", commentText]);
      rowDotRgb.push(null);
    }

    if (body.length === 0) {
      body.push(["—", "Aucune donnée"]);
      rowDotRgb.push(null);
    }

    const colLabelW = 35;
    const groupValueTextInnerW = contentW - colLabelW - 7 - 2.5;

    (doc as any).autoTable({
      startY: y,
      body,
      margin: { left: margin, right: margin },
      tableLineColor: [210, 210, 210],
      tableLineWidth: lw,
      columnStyles: {
        0: { cellWidth: colLabelW, fontStyle: "normal", textColor: [80, 80, 80], valign: "middle" },
        1: { cellWidth: contentW - colLabelW, valign: "top", halign: "left" },
      },
      styles: {
        fontSize: 8.5,
        cellPadding: { top: 2.5, bottom: 2.5, left: 7, right: 2.5 },
        textColor: textColor,
        lineColor: [210, 210, 210],
        lineWidth: lw,
        overflow: "linebreak",
        fillColor: [255, 255, 255],
      },
      theme: "grid",
      didParseCell: (cellData: {
        section: string;
        column: { index: number };
        cell: { raw: unknown; text: string[]; styles: { textColor: number[] } };
      }) => {
        if (cellData.section !== "body" || cellData.column.index !== 1) return;
        if (!isPdfPersonCommentCell(cellData.cell.raw)) return;
        const raw = cellData.cell.raw;
        doc.setFontSize(8.5);
        doc.setFont("helvetica", "normal");
        const combined = `${raw.content}  ${pdfCommentTextForDisplay(raw.pdfPersonComment)}`;
        cellData.cell.text = doc.splitTextToSize(combined, groupValueTextInnerW);
        cellData.cell.styles.textColor = [255, 255, 255];
      },
      didDrawCell: (cellData: any) => {
        if (cellData.section !== "body" || cellData.column.index !== 1) return;
        const rgb = rowDotRgb[cellData.row.index];
        if (rgb) {
          const cx = cellData.cell.x + 3;
          const cy = cellData.cell.y + cellData.cell.height / 2;
          doc.setFillColor(...rgb);
          doc.circle(cx, cy, 1.3, "F");
        }
        if (isPdfPersonCommentCell(cellData.cell.raw)) {
          const raw = cellData.cell.raw;
          drawPersonNameWithItalicComment(
            doc,
            cellData.cell,
            raw.content,
            raw.pdfPersonComment,
            textColor,
          );
        }
      },
    });
    y = (doc as any).lastAutoTable.finalY + 8;
  }

  if (data.groups.length === 0) {
    doc.setFontSize(9);
    doc.setTextColor(...mutedColor);
    doc.text("Aucun groupe", margin + 2, y);
  }

  // ── Footer pagination ──
  const pageCount = doc.getNumberOfPages();
  doc.setFontSize(7);
  doc.setTextColor(...mutedColor);
  for (let i = 1; i <= pageCount; i++) {
    doc.setPage(i);
    const ph = doc.internal.pageSize.getHeight();
    const footerLeft = [data.companyName, data.name, formatDate(data.date)]
      .filter(Boolean)
      .join(" — ");
    doc.text(`${footerLeft} — Page ${i}/${pageCount}`, margin, ph - 6);
  }

  const blob = doc.output("blob");
  const url = URL.createObjectURL(blob);
  window.open(url, "_blank", "noopener,noreferrer");
}
